/* eslint-disable compat/compat */
/* eslint-disable no-bitwise */
import { ZipEntry } from './zip-entry';

// Zip entry constants used in zip part headers
const SIGNATURE = {
  LOCAL_FILE_HEADER: 'PK\x03\x04',
  CENTRAL_FILE_HEADER: 'PK\x01\x02',
  CENTRAL_DIRECTORY_END: 'PK\x05\x06',
  DIR_FILE_ATTR: '\x10\x00\xFD\x41',
  NON_DIR_FILE_ATTR: '\x00\x00\xB4\x81'
};

export class IdsZip {
  // folder root
  root = '';

  // zip entries
  entries = {};

  // byte count (size)
  bytesWritten = 0;

  // collection of encoded zip file records, concatenated at the end
  dataArray = [];

  /**
   * Add a zip entry
   * @param {string} name file name
   * @param {string} data string data
   */
  file(name, data) {
    name = this.root + name;
    this.addEntry(name, data, false);
  }

  /**
   * Creates Zip file containing all entries
   * @param {string} mimeType mime type
   * @returns {Blob} Blob of zip file
   */
  zip(mimeType) {
    return this.createZipFile(Object.values(this.entries), mimeType);
  }

  /**
   * Create folder zip entry
   * @param {string} name folder name
   * @returns {ZipEntry} folder zip entry
   */
  addFolderEntry(name) {
    if (!this.entries[name]) {
      this.addEntry(name, null, true);
    }

    return this.entries[name];
  }

  /**
   * Creates zip entry
   * @param {string} name file name
   * @param {string} data content data
   * @param {boolean} isDir true if entry is a folder
   */
  addEntry(name, data, isDir) {
    // create parent folders
    const parent = IdsZip.isNested(name);
    if (parent) this.addFolderEntry(parent);

    // if folder or data empty, initialize as empty Uint8Array
    if (!!isDir || !data || data.length === 0) {
      data = new Uint8Array();
    }

    const zipEntry = new ZipEntry(name, data, isDir);
    this.entries[name] = zipEntry;
  }

  /**
   * Checks if path is nested in folders
   * @param {string} path path name
   * @returns {string} parent folder
   */
  static isNested(path) {
    if (path.slice(-1) === '/') {
      path = path.substring(0, path.length - 1);
    }
    const lastSlash = path.lastIndexOf('/');
    return (lastSlash > 0) ? path.substring(0, lastSlash) : '';
  }

  /**
   * Process all zip entries into a Blob
   * @param {Array<ZipEntry>} entries array of zip entries
   * @param {string} mimeType mime type
   * @returns {Blob} Blob of zip file
   */
  createZipFile(entries, mimeType) {
    const entryDirs = [];
    const processEntry = (entry, entryOffset) => {
      const content = entry.processData();
      // eslint-disable-next-line no-shadow
      const generateHeaderPartial = (entry) => {
        const compression = entry.compression;
        const date = entry.date;
        let modTime;
        let modDate;

        // calc last modified time
        modTime = date.getUTCHours();
        modTime <<= 6;
        modTime |= date.getUTCMinutes();
        modTime <<= 5;
        modTime |= date.getUTCSeconds() / 2;

        // calc last modified date
        modDate = date.getUTCFullYear() - 1980;
        modDate <<= 4;
        modDate |= (date.getUTCMonth() + 1);
        modDate <<= 5;
        modDate |= date.getUTCDate();

        return [
        // Version needed to extract (minimum)
          '\x0A\x00',
          // General purpose bit flag
          '\x00\x00',
          // Compression method
          compression,
          // File last modification time
          IdsZip.decToHex(modTime, 2),
          // File last modification date
          IdsZip.decToHex(modDate, 2),
          // CRC-32
          IdsZip.decToHex(entry.crc32, 4),
          // Compressed size
          IdsZip.decToHex(entry.compressedSize, 4),
          // Uncompressed size
          IdsZip.decToHex(entry.uncompressedSize, 4),
          // File name length (n)
          IdsZip.decToHex(entry.name.length, 2),
          // Extra field length (m)
          '\x00\x00'
        ].join('');
      };

      const headerPartial = generateHeaderPartial(entry);
      const localFileHeader = IdsZip.generateLocalFileHeader(headerPartial, entry);
      // eslint-disable-next-line no-shadow
      const generateCentralDirectoryFileHeader = (entry, header, offset) => {
        // UNIX, version 3.0
        const VERSION_MADE_BY = 0x031E;
        // Reflects no OS permissions set for (read, write, execute), only difference is flag for directory
        const EXT_FILE_ATTR = entry.dir ? SIGNATURE.DIR_FILE_ATTR : SIGNATURE.NON_DIR_FILE_ATTR;

        return [
          // Central directory file header signature
          SIGNATURE.CENTRAL_FILE_HEADER,
          // Version made by
          IdsZip.decToHex(VERSION_MADE_BY, 2),
          // generateHeaderPartial
          header,
          // File comment length (k)
          '\x00\x00',
          // Disk number where file starts
          '\x00\x00',
          // Internal file attributes
          '\x00\x00',
          // External file attributes
          EXT_FILE_ATTR,
          // Relative offset of local file header.
          IdsZip.decToHex(offset, 4),
          // File name
          entry.name,
          // Extra field
          '',
          // File comment
          ''
        ].join('');
      };

      const centralDirectoryFileHeader = generateCentralDirectoryFileHeader(entry, headerPartial, entryOffset);

      return {
        localFileHeader,
        centralDirectoryFileHeader,
        content
      };
    };

    entries.forEach((entry) => {
      const entryParts = processEntry(entry, this.bytesWritten);
      // write entry header
      this.writeData(entryParts.localFileHeader);
      // write entry content
      this.writeData(entryParts.content);
      // cache entry dir record for central directory section
      entryDirs.push(entryParts.centralDirectoryFileHeader);
    });

    // write EOCD
    this.closeZipFile(entryDirs);
    const dataArray = IdsZip.concat(this.dataArray);

    return new Blob([dataArray.buffer], { type: mimeType });
  }

  /**
   * Prepares End of Central Directory record to end zip package
   * @param {Array<string>} entryDirs dir zip entries
   */
  closeZipFile(entryDirs) {
    const localDirSize = this.bytesWritten;

    for (let i = 0; i < entryDirs.length; i++) {
      this.writeData(entryDirs[i]);
    }

    const centralDirSize = this.bytesWritten - localDirSize;
    // eslint-disable-next-line no-shadow
    const generateEndOfCentralDirectory = (entriesCount, centralDirSize, offset) => [
      // End of central directory signature
      SIGNATURE.CENTRAL_DIRECTORY_END,
      // Number of this disk
      '\x00\x00',
      // Disk where central directory starts
      '\x00\x00',
      // Number of central directory records on this disk
      IdsZip.decToHex(entriesCount, 2),
      // Total number of central directory records
      IdsZip.decToHex(entriesCount, 2),
      // Size of central directory (4 bytes)
      IdsZip.decToHex(centralDirSize, 4),
      // Offset of start of central directory, relative to start of archive
      IdsZip.decToHex(offset, 4),
      // Comment length (n)
      '\x00\x00',
      // Comment
      ''
    ].join('');
    const dirEnd = generateEndOfCentralDirectory(
      entryDirs.length,
      centralDirSize,
      localDirSize
    );

    this.writeData(dirEnd);
  }

  /**
   * Appends and transforms data to data array
   * Also tracks current size of all entries
   * @param {any} data string or Uint8Array
   */
  writeData(data) {
    if (data && data.length) {
      this.dataArray.push(IdsZip.transform(data));
      this.bytesWritten += data.length;
    }
  }

  /**
   * Transforms string data into Uint8Array
   * @param {string | Uint8Array} data data
   * @returns {Uint8Array} encoded data
   */
  static transform(data) {
    if (typeof data !== 'string') return data;

    const output = new Uint8Array(data.length);
    for (let i = 0; i < data.length; ++i) {
      // trims charCode to 8 bits
      output[i] = data.charCodeAt(i) & 0xFF;
    }

    return output;
  }

  /**
   * Flattens data array collection
   * @param {Array<Uint8Array>} dataArray data array of all zip entries
   * @returns {Uint8Array} flattend dataArray
   */
  static concat(dataArray) {
    const totalLength = dataArray.reduce((total, data) => total + data.length, 0);
    const res = new Uint8Array(totalLength);
    let index = 0;

    for (let i = 0; i < dataArray.length; i++) {
      res.set(dataArray[i], index);
      index += dataArray[i].length;
    }

    return res;
  }

  /**
   * Create Locale File Header string
   * @see {@link https://docs.fileformat.com/compression/zip/#local-file-header}
   * @param {string} header header partial
   * @param {ZipEntry} entry zip entry
   * @returns {string} Locale File Header string
   */
  static generateLocalFileHeader(header, entry) {
    return SIGNATURE.LOCAL_FILE_HEADER + header + entry.name;
  }

  /**
   * Convert decimal value into a padded 8 bit hex strings
   * Returns hex string in little-endian order
   * @param {number} dec the number to convert.
   * @param {number} bytes the number of bytes to generate.
   * @returns {string} the result.
   */
  static decToHex(dec, bytes) {
    let hex = '';

    for (let i = 0; i < bytes; i++) {
      // get char code from first 8 bits of decimal value
      hex += String.fromCharCode(dec & 0xFF);
      // shift decimal by 8 bits
      dec >>>= 8;
    }

    return hex;
  }
}
